/**
 * DONMA-2 · Rota parçası yüklemesine ZAMAN AŞIMI.
 *
 * ── KUSUR, KULLANICININ GÖZLEMİYLE KAPANDI ───────────────────────────────
 *
 * 16 Eylül 2026: "uygulama uzun süre açık kalınca donuyor, kapatıp açmak
 * gerekiyor." Cihazda ölçüldü — dokunuş geliyordu, `click` üretiliyordu,
 * React kökü bağlıydı, ekranda örtü yoktu. Yani her şey canlıydı, ekran
 * değişmiyordu.
 *
 * Teşhisi kullanıcının kendi gözlemi kapattı:
 *
 *     "ACİL butonu ve EN/TR çevirisi çalışıyor, harici her şey donuyor."
 *
 * O ikisinin ortak yanı: İKİSİ DE REACT ROUTER'DAN GEÇMİYOR.
 *   • ACİL   → düz `<a href>`, `preventDefault` YOK → TAM SAYFA YENİLEMESİ
 *   • EN/TR  → hiç gezinmiyor, yalnızca durum değişimi
 *
 * Geri kalan her şey `<Link>`/`navigate()` ile gidiyor ve her rota AYRI BİR
 * PARÇA olarak `import()` ile iniyor. Yani ölü olan tek şey PARÇA YÜKLEME.
 * ACİL'in çalışmasının sebebi de bu: o bir yenileme — kullanıcının uygulamayı
 * kapatıp açmasıyla aynı şey.
 *
 * ── DELİK ────────────────────────────────────────────────────────────────
 *
 * `App.js`teki `lazyRetry` yalnızca REDDEDİLEN yüklemeyi yakalıyordu:
 *
 *     factory().catch(...)   // reddedilirse sayfayı yenile
 *
 * Ama yükleme HİÇ CEVAP VERMEZSE (ne çözülür ne reddedilir) `catch` hiç
 * çalışmaz. React `Suspense`te sonsuza kadar bekler; React Router beklerken
 * ÖNCEKİ EKRANI göstermeye devam eder. Kullanıcının gördüğü "donma" tam
 * olarak budur: ekran duruyor, uygulama çalışıyor, hiçbir şey olmuyor.
 *
 * ⚠️ Sessiz bir asılma, hata veren bir asılmadan daha kötüdür: hatanın bir
 * yakalayıcısı vardı, sessizliğin yoktu.
 *
 * ── DÜRÜST SINIR ─────────────────────────────────────────────────────────
 *
 * Bu dosya asılmayı ÖNLEMİYOR, ondan ÇIKIYOR. Parçanın niçin asıldığı ayrı
 * bir soru (uzun oturumda WebView'in yükleyicisi bozuluyor; donma anında
 * `/api/sightings` istekleri de "WebKit dahili bir hatayla karşılaştı" diye
 * düşüyordu). Buradaki kural yalnızca şunu garanti ediyor: sessiz bekleyiş
 * sonsuz sürmesin, kendini bir hataya dönüştürsün ve üstteki yenileme
 * mantığı devreye girebilsin.
 *
 * 16 Eyl 2026 · DONMA-2
 */

/** Rota parçası için tanınan süre. */
export const PARCA_SURESI_MS = 12000;

/**
 * Sözveriyi süreye bağlar: süre dolarsa REDDEDER.
 *
 * ⚠️ Zamanlayıcı HER İKİ dalda da temizleniyor. Temizlenmezse yükleme
 * başarılı olsa bile zamanlayıcı yaşamaya devam eder; her gezinmede bir tane
 * birikir ve uzun oturumda — yani kusurun tam çıktığı yerde — gereksiz yük
 * olur. Kusuru düzeltirken aynı sınıftan bir kusur üretmek olurdu.
 *
 * ⚠️ `ms` geçersizse (0, negatif, sayı değil) zaman aşımı HİÇ kurulmuyor:
 * süresiz bekleme, ANINDA reddetmekten iyidir. Yanlış bir yapılandırma
 * yüzünden çalışan yüklemeleri kesmek, kusuru genişletmek olurdu.
 */
export function zamanAsimli(sozveri, ms = PARCA_SURESI_MS) {
  const gecerli = typeof ms === "number" && Number.isFinite(ms) && ms > 0;
  if (!gecerli) return Promise.resolve(sozveri);

  return new Promise((coz, red) => {
    const zamanlayici = setTimeout(() => {
      red(new Error("PARCA_ZAMAN_ASIMI"));
    }, ms);

    Promise.resolve(sozveri).then(
      (deger) => { clearTimeout(zamanlayici); coz(deger); },
      (hata) => { clearTimeout(zamanlayici); red(hata); },
    );
  });
}

/** Bir oturumda izin verilen en fazla yenileme. */
export const EN_FAZLA_YENILEME = 2;

/**
 * Yenileme yapılmalı mı — ve sayacı ilerletir.
 *
 * ⚠️ SAYI SINIRI, SÜRE SINIRI DEĞİL. Önceki hâl "10 saniyede bir kez"
 * diyordu; zaman aşımı 12 saniye olunca o pencere HER SEFERİNDE açık kalırdı
 * ve kalıcı bir bozuklukta uygulama 12 saniyede bir kendini yenileyip
 * dururdu — donmadan beter bir döngü. Sayaçla en fazla iki kez deneniyor,
 * sonra hata yüzeye çıkıyor ve açılış hata perdesi kendini anlatıyor.
 *
 * `sessionStorage` yoksa (gizli sekme, kapalı depolama) `false` dönüyor:
 * sayacı tutamadığımız yerde yenilemek, sayısız yenileme demektir.
 */
export function yenilemeHakkiVar(depo) {
  try {
    const s = depo || (typeof sessionStorage !== "undefined" ? sessionStorage : null);
    if (!s) return false;
    const sayi = Number(s.getItem("parcaYenileme") || 0);
    if (!Number.isFinite(sayi) || sayi >= EN_FAZLA_YENILEME) return false;
    s.setItem("parcaYenileme", String(sayi + 1));
    return true;
  } catch {
    return false;
  }
}
